import { type ReactNode } from 'react';
import { createRoot, type Root } from 'react-dom/client';

/**
 * 见飞书问题：https://project.feishu.cn/b2rl2h/issue/detail/6978310510
 *
 * 背景：
 * - React 18 会在 createRoot 容器、document、body/html 等节点上写入
 *   `_reactListening*` 标记，用来避免重复注册合成事件监听。
 * - wujie 卸载子应用时会回收真实的原生 listener，但不会清理这些 React 私有标记。
 * - `document.addEventListener('click', ...)` 仍能监听到原生点击，
 *   说明丢失的是 React 的合成事件分发，而不是浏览器原生冒泡链路。
 *
 * 现象：
 * - root 容器内点击大多正常；
 * - 挂到 body 上的 Portal 弹层（Modal、Popover 等）点击失效。
 */
const cleanReactListeningMarkers = (node: Node | null | undefined) => {
    if (!node) {
        return;
    }
    for (const key of Object.keys(node)) {
        if (key.startsWith('_reactListening')) {
            delete (node as unknown as Record<string, unknown>)[key];
        }
    }
};

/**
 * 渲染可同时作为普通页面和非保活 wujie 子应用（alive = false）运行的入口。
 */
export const renderWujieView = (node: ReactNode, containerId = 'root') => {
    let root: Root | null = null;

    const mount = () => {
        const container = document.getElementById(containerId);
        if (!container) {
            return;
        }
        if (!root) {
            root = createRoot(container);
        }
        root.render(node);
    };

    if (window.__POWERED_BY_WUJIE__) {
        window.__WUJIE_MOUNT = mount;
        window.__WUJIE_UNMOUNT = () => {
            root?.unmount();
            root = null;

            cleanReactListeningMarkers(document);
            cleanReactListeningMarkers(document.body);
            cleanReactListeningMarkers(document.documentElement);
        };
    } else {
        mount();
    }
};
